import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CreateSlideWizard } from "./CreateSlideWizard";
import { GenerationProgress } from "./GenerationProgress";
import { useCreateSlideGenerator } from "../hooks/useCreateSlideGenerator";
import {
  canUseDialog,
  openCreateSlidesDialog,
  type CreateSlidesDialogHandlers,
} from "../hooks/openCreateSlidesDialog";
import { insertSlideFromBase64 } from "../hooks/slideInsert";
import { insertSlideFromModel } from "../hooks/insertSlideModel";
import { createApiGenerator, toGenerateRequest } from "../service/apiGenerator";
import { createTemplateGenerator } from "../service/templateGenerator";
import { fetchSlideOutline } from "../service/templatesApi";
import { requestSlideTemplatesSasInfo } from "../../slide-templates/services/slideTemplatesApi";
import { DEFAULT_SLIDE_DIMENSIONS, TEMPLATE_LIBRARY_TYPES } from "../service/templateCatalogApi";
import { readThemeColors } from "../hooks/themeColors";
import {
  buildPalette,
  intentToCategory,
  slideModelFromOutline,
  type SlidePalette,
} from "../hooks/slideModel";
import {
  slidePptxBase64,
  type IntentTag,
  type SlideBrief,
  type SlideDimensions,
  type SlideXmlResult,
  type ThemeColors,
} from "../service/slideGenerationApi";
// Reused rather than re-derived: the backend's freehand slide is the SAME
// problem Convert-to-Infographic already solved — an AI-authored `<p:spTree>`
// fragment with no `.pptx` of its own to live in. buildInfographicSlideBase64
// wraps it into a real, insertable one-slide package (PptxGenJS skeleton +
// fflate slide-XML swap, with the composer-XML crash-avoidance repairs that
// pipeline already carries); readSlideDimensionsEmu sizes that skeleton to
// the live deck so PowerPoint never has to reconcile a mismatched slide size.
import { buildInfographicSlideBase64 } from "../../Infographic/services/infographicInsert";
import { readSlideDimensionsEmu } from "../../Infographic/services/infographicContent";
import { DEFAULT_CONFIG, type CreateSlideConfig } from "../hooks/types";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { useDeckorateHttp } from "../../../hooks/useDeckorateHttp";
import { config as appConfig } from "../../../../../shared/config/environment";
import styles from "./CreateSlide.module.css";

interface CreateSlideTabProps {
  readonly onBack?: () => void;
}

/**
 * "Create slides" tab. The wizard (source → details → design → review) opens in
 * an Office Dialog modal over PowerPoint; on "Generate" the config comes back to
 * the pane, where generation + slide insertion run so the user watches progress
 * live. When the Dialog API isn't available (browser/tests) the wizard falls
 * back to an inline pane flow. When `onBack` is provided a Deckorate header is
 * shown so the user can return to the module landing (matches FormatTools).
 */
export function CreateSlideTab({ onBack }: Readonly<CreateSlideTabProps>) {
  const body = canUseDialog() ? <CreateSlideLauncher onBack={onBack} /> : <CreateSlideInline />;
  if (!onBack) return body;
  return (
    <div className={styles.wrap}>
      <ModuleHeader title="Create Slides" subtitle="Deckorate" onBack={onBack} />
      {body}
    </div>
  );
}

/**
 * Inserts one finished slide into the active deck.
 *
 * Priority order:
 *   1. The backend's own designed base64 `.pptx`, inserted verbatim — the
 *      contract's intended preferred path (forward-compat: DeckorateAPI
 *      doesn't send this field yet, so this branch is currently a no-op, but
 *      costs nothing to keep ready for when it does).
 *   2. The backend's actual per-slide OOXML (`xml`, an inner `<p:spTree>`
 *      shape tree) — what the API returns TODAY. This is the AI's real
 *      design for the slide, so it's rendered rather than discarded; the
 *      structured outline model below is a stand-in for this, not the
 *      normal case.
 *   3. Only when the backend gave us nothing usable (no xml, or it failed to
 *      package/insert) does this fall back to the structured model built
 *      from the outline's title + bullets — graceful degradation for a
 *      genuine backend failure, not the default rendering path.
 */
async function insertGeneratedSlide(
  result: SlideXmlResult,
  brief: SlideBrief | undefined,
  palette: SlidePalette,
  slideDims: SlideDimensions,
): Promise<void> {
  const pptx = slidePptxBase64(result);
  if (pptx) {
    console.info(`[create-slide] "${result.title}" → designed PPTX`);
    return insertSlideFromBase64(pptx);
  }

  // `valid === false` is the backend's own well-formedness check failing —
  // skip straight to the fallback rather than paying for a package/insert
  // round trip already known to fail.
  if (result.xml && result.valid !== false) {
    try {
      const base64 = await buildInfographicSlideBase64(result.xml, slideDims);
      console.info(`[create-slide] "${result.title}" → AI-designed OOXML`);
      return await insertSlideFromBase64(base64, "UseDestinationTheme");
    } catch (e) {
      console.warn(
        `[create-slide] "${result.title}" OOXML render failed, falling back to the outline model:`,
        e,
      );
    }
  }

  console.info(`[create-slide] "${result.title}" → outline points only`);
  const category = intentToCategory(brief?.intent_tag ?? result.intent_tag);
  const model = slideModelFromOutline(
    brief?.title ?? result.title,
    brief?.main_points ?? [],
    category,
    palette,
  );
  return insertSlideFromModel(model);
}

/**
 * The two things the wizard window needs the PANE to do while it's still
 * open, on the templates path: generate the outline (an authorised call) and
 * mint the template library's SAS (needs the authorised HttpClient). See
 * hooks/openCreateSlidesDialog.ts.
 */
function useDialogHandlers(): CreateSlidesDialogHandlers {
  const http = useDeckorateHttp();
  return useMemo(
    () => ({
      requestOutline: (config) =>
        // Same body the freehand path sends, so the outline the user picks
        // layouts for is built from exactly the same inputs.
        fetchSlideOutline(toGenerateRequest(config), {
          baseUrl: appConfig.deckorateApiBaseUrl,
          authorizedFetch: http.fetch,
        }),
      requestTemplateCatalog: async () => {
        // One mint per registered library: blob-sas scopes a directory SAS to
        // a single folder, and the outline ranker scores over all of them (see
        // service/templateCatalogApi.ts). Settled, not all-or-nothing, so an
        // unregistered folder still leaves the others browsable.
        const results = await Promise.allSettled(
          TEMPLATE_LIBRARY_TYPES.map((templateType) =>
            requestSlideTemplatesSasInfo(http, {}, templateType),
          ),
        );
        const libraries = results.flatMap((result) =>
          result.status === "fulfilled"
            ? [{ sasUrl: result.value.directorySasUrl, manifestBlobPath: result.value.manifestBlobPath }]
            : [],
        );
        if (libraries.length === 0) {
          const rejection = results.find((result) => result.status === "rejected");
          throw rejection?.status === "rejected"
            ? (rejection.reason as Error)
            : new Error("No slide template library is registered for this client.");
        }
        return { libraries };
      },
    }),
    [http],
  );
}

/**
 * Builds the pane-side generators: the freehand one streams from the
 * DeckorateAPI and inserts each slide into the active deck; the templates one
 * fits the approved plan and patches each chosen layout. Both drive the same
 * progress view.
 */
function usePaneGenerator() {
  // Shared authorised client (bearer + APIM key + 401 refresh + telemetry),
  // bound to the Deckorate backend (config.deckorateApiBaseUrl) — same flow as
  // every other Deckorate feature hook.
  const http = useDeckorateHttp();

  // Read the deck's theme colours (pane-only host API) so generated slides
  // match the active presentation's brand. Degrades to undefined on failure.
  const [themeColors, setThemeColors] = useState<ThemeColors | undefined>();
  useEffect(() => {
    let cancelled = false;
    readThemeColors()
      .then((t) => {
        if (!cancelled && t) setThemeColors(t);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Read the deck's live slide size ONCE per session (not per slide) so every
  // AI-designed slide this run inserts is packaged at the size PowerPoint
  // already expects — readSlideDimensionsEmu never rejects, falling back to
  // the 16:9 default itself, so this only ever replaces the initial default.
  const [slideDims, setSlideDims] = useState<SlideDimensions>(DEFAULT_SLIDE_DIMENSIONS);
  useEffect(() => {
    let cancelled = false;
    readSlideDimensionsEmu()
      .then((d) => {
        if (!cancelled) setSlideDims(d);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  // Colour palette derived from the deck's theme, so model-rendered slides
  // (the no-PPTX fallback) match the deck's brand.
  const palette = useMemo(() => buildPalette(themeColors), [themeColors]);

  const generator = useMemo(
    () =>
      createApiGenerator({
        baseUrl: appConfig.deckorateApiBaseUrl,
        authorizedFetch: http.fetch,
        themeColors,
        insertSlide: (result, brief) => insertGeneratedSlide(result, brief, palette, slideDims),
      }),
    [http, themeColors, palette, slideDims],
  );

  const templateGenerator = useMemo(
    () =>
      createTemplateGenerator({
        baseUrl: appConfig.deckorateApiBaseUrl,
        http,
        // A slide whose template can't be fetched still gets built, from the
        // same structured model the freehand path's no-PPTX fallback uses —
        // so an unreadable template library costs the reviewed layout, not
        // the slide.
        renderWithoutTemplate: (slide) =>
          insertSlideFromModel(
            slideModelFromOutline(
              slide.title,
              slide.main_points,
              intentToCategory(slide.intent_tag as IntentTag),
              palette,
            ),
          ),
      }),
    [http, palette],
  );

  return useCreateSlideGenerator(generator, templateGenerator);
}

/* ── Host path: wizard in the dialog modal, generation in the pane ── */

interface CreateSlideLauncherProps {
  readonly onBack?: () => void;
}

function CreateSlideLauncher({ onBack }: Readonly<CreateSlideLauncherProps>) {
  const gen = usePaneGenerator();
  const handlers = useDialogHandlers();
  const [topic, setTopic] = useState("");
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const launch = useCallback(async () => {
    setOpening(true);
    setError(null);
    try {
      const result = await openCreateSlidesDialog(handlers);
      if (result) {
        const cfg = result.config;
        setTopic(result.plan?.title || cfg.topic || cfg.documents[0]?.name || "your deck");
        // A plan means the user went through the templates path and approved
        // a layout per slide; without one this is the freehand path.
        if (result.plan) gen.startTemplates(result.plan);
        else gen.start(cfg);
      } else {
        // The user cancelled/closed the wizard without configuring anything —
        // there's no button left on this tab to relaunch it, so return to the
        // Deckorate home instead of stranding the user on a dead idle screen.
        onBack?.();
      }
    } catch (e) {
      // Genuine failure to open the dialog — log the detail and show the user a
      // clear, actionable message instead of silently doing nothing.
      console.error("[create-slide] failed to open the Create Slides wizard:", e);
      setError(
        e instanceof Error
          ? e.message
          : "Couldn't open the Create Slides window. Please try again.",
      );
    } finally {
      setOpening(false);
    }
  }, [gen, onBack, handlers]);

  // Opening the tab opens the wizard dialog immediately — no separate
  // "Create slides" click needed. Guarded so it fires exactly once per
  // mount even under React StrictMode's double-invoke.
  const startedRef = useRef(false);
  useEffect(() => {
    if (!startedRef.current) {
      startedRef.current = true;
      launch().catch(() => undefined);
    }
  }, []);

  // "Create another" (GenerationProgress's onRestart) needs to do more than
  // clear generator state: `gen.reset()` alone drops `phase` back to "idle",
  // but this component only ever RE-OPENS the wizard from the mount effect
  // above (guarded by startedRef so it can't double-fire). Without also
  // calling `launch()` here, "idle" with no dialog open just renders the
  // blank shell below forever — the bug this fixes.
  const restart = useCallback(() => {
    gen.reset();
    launch().catch(() => undefined);
  }, [gen, launch]);

  if (gen.phase !== "idle") {
    return (
      <GenerationProgress
        topic={topic}
        current={gen.current}
        total={gen.total}
        plans={gen.plans}
        phase={gen.phase}
        error={gen.error}
        onCancel={gen.cancel}
        onRestart={restart}
      />
    );
  }

  // Nothing to show while the dialog is opening/open — the wizard launches
  // immediately on mount with no intermediate idle screen. Only a genuine
  // failure to open the dialog gets a visible message + retry button.
  if (!error) return <div className={styles.launch} />;

  return (
    <div className={styles.launch}>
      <p className={styles.error} role="alert">
        {error}
      </p>
      <button
        type="button"
        className={styles.btnPrimary}
        onClick={() => {
          launch().catch(() => undefined);
        }}
        disabled={opening}
      >
        {opening ? "Opening…" : "Retry"}
      </button>
    </div>
  );
}

/* ── Fallback: inline wizard (no Dialog API — browser/tests) ── */

function CreateSlideInline() {
  const gen = usePaneGenerator();
  const [config, setConfig] = useState<CreateSlideConfig>(DEFAULT_CONFIG);

  if (gen.phase !== "idle") {
    return (
      <GenerationProgress
        topic={config.topic}
        current={gen.current}
        total={gen.total}
        plans={gen.plans}
        phase={gen.phase}
        error={gen.error}
        onCancel={gen.cancel}
        onRestart={gen.reset}
      />
    );
  }

  return (
    <CreateSlideWizard
      config={config}
      onChange={(patch) => setConfig((c) => ({ ...c, ...patch }))}
      onGenerate={() => gen.start(config)}
    />
  );
}
